import React from "react";
import PropTypes from "prop-types";
import { Icon } from "@iconify/react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const CycleTasksEditor = ({
  calendarBased,
  cycleRows,
  cycleRowErrors,
  cycleTaskOptions,
  baseTaskLabel,
  baseMeterId = null,
  isCycleLoading = false,
  onDragEnd,
  onRowChange,
  onDeleteRow,
  onAddRow,
}) => (
  <div className="mt-4">
    {cycleRowErrors.general && (
      <div className="alert alert-danger mt-3 mb-0" role="alert">
        {cycleRowErrors.general}
      </div>
    )}
    {!baseMeterId && calendarBased === 0 && (
      <div className="alert alert-warning mt-3 mb-0" role="alert">
        Select a meter to enable cycle task selection.
      </div>
    )}
    <label className="form-label mt-16">Cycle Tasks</label>
    <div style={{ maxHeight: "calc(100vh - 620px)", overflow: "auto" }}>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="cycleRows">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {cycleRows.map((row, index) => (
                <Draggable
                  key={row.clientId}
                  draggableId={row.clientId}
                  index={index}
                  isDragDisabled={isCycleLoading}
                >
                  {(dragProvided) => (
                    <div
                      className="border rounded p-3 mb-3 bg-body-tertiary"
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                    >
                      <div className="d-flex flex-wrap gap-3 align-items-start">
                        <button
                          type="button"
                          className="btn btn-link text-muted p-0"
                          style={{ cursor: isCycleLoading ? "not-allowed" : "grab" }}
                          {...dragProvided.dragHandleProps}
                          disabled={isCycleLoading}
                        >
                          <Icon icon="tabler:grip-vertical" className="fs-4 mt-6" />
                        </button>
                        <div className="flex-grow-1">
                          <select
                            className="form-select"
                            value={row.taskId || ""}
                            onChange={(e) => onRowChange(row.clientId, "taskId", e.target.value)}
                            disabled={isCycleLoading || !baseMeterId && calendarBased === 0}
                          >
                            <option value="">Select Task</option>
                            {cycleTaskOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                            {row.taskId && !cycleTaskOptions.find((opt) => opt.value === row.taskId) && (
                              <option value={row.taskId}>
                                {row.taskId === "__CURRENT_TASK__" ? baseTaskLabel : `Task ${row.taskId}`}
                              </option>
                            )}
                          </select>
                          {cycleRowErrors[`${row.clientId}-taskId`] && (
                            <div className="text-danger small mt-1">
                              {cycleRowErrors[`${row.clientId}-taskId`]}
                            </div>
                          )}
                        </div>
                        <div className="flex-shrink-0" style={{ width: 140 }}>
                          <input
                            type="number"
                            min="1"
                            className="form-control"
                            value={row.interval}
                            onChange={(e) => onRowChange(row.clientId, "interval", e.target.value)}
                            // disabled={isCycleLoading}
                            disabled={true}
                          />
                          {cycleRowErrors[`${row.clientId}-interval`] && (
                            <div className="text-danger small mt-1">
                              {cycleRowErrors[`${row.clientId}-interval`]}
                            </div>
                          )}
                        </div>
                        <div className="flex-shrink-0 d-flex align-items-center">
                          <button
                            type="button"
                            className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                            style={{ marginTop: "4px" }}
                            onClick={() => onDeleteRow(row.clientId)}
                            disabled={isCycleLoading || cycleRows.length <= 1}
                          >
                            <Icon icon="ic:baseline-delete-outline" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
    <div className="d-flex justify-content-start align-items-center mt-3">
      <button
        type="button"
        className="btn btn-link px-0 text-decoration-none"
        onClick={onAddRow}
        disabled={isCycleLoading}
      >
        + Add Cycle Task
      </button>
    </div>
  </div>
);

CycleTasksEditor.propTypes = {
  cycleRows: PropTypes.arrayOf(PropTypes.object).isRequired,
  cycleRowErrors: PropTypes.object.isRequired,
  cycleTaskOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  baseTaskLabel: PropTypes.string.isRequired,
  baseMeterId: PropTypes.number,
  isCycleLoading: PropTypes.bool,
  onDragEnd: PropTypes.func.isRequired,
  onRowChange: PropTypes.func.isRequired,
  onDeleteRow: PropTypes.func.isRequired,
  onAddRow: PropTypes.func.isRequired,
};

export default CycleTasksEditor;
